import { ReactNode, useState, useMemo, useEffect } from 'react'
import { BookingContext } from './BookingContext'
import { BookingStep, CustomerInfo, SelectedDateTime } from './types'
import { Service } from '@w/lib/api'

// Props that dashboard will provide
export interface DashboardBookingProviderProps {
  children: ReactNode
  services: Service[]
  availability: Record<string, string[]>
  config: {
    businessApiKey: string
    apiBaseUrl: string
    widgetConfig: {
      enabled_services?: string[]
      field_labels?: Record<string, string>
      custom_questions?: any[]
      [key: string]: any
    }
    businessInfo: {
      id: string
      name: string
      slug: string
      [key: string]: any
    }
    styling?: {
      primaryColor?: string
      theme?: string
      borderRadius?: number
    }
  }
  // Configuration mode flag - when true, show static preview for configuration
  configurationMode?: boolean
  // CONTROLLED STATE: Preview UI state (ephemeral, not saved to draft)
  controlledState?: {
    currentStep?: BookingStep
    selectedService?: Service | null
    selectedDate?: string | null
    selectedTimeSlot?: string | null
    selectedDateTime?: SelectedDateTime | null
    serviceAddress?: string
    currentMonth?: Date
    customerInfo?: CustomerInfo | null
    selectedFieldId?: string | null
  }
  // CONTROLLED CALLBACKS: For updating preview state
  onStateChange?: (updates: Partial<DashboardBookingProviderProps['controlledState']>) => void
  // CONFIG CHANGES: For updating field labels/placeholders/messages/configs (saved to draft)
  onConfigChange?: (updates: {
    field_labels?: Record<string, string>
    success_message?: string
    field_configs?: Record<string, any>
    custom_questions?: any[]
  }) => void
}

export function DashboardBookingProvider({
  children,
  services,
  availability,
  config,
  configurationMode = false,
  controlledState = {},
  onStateChange,
  onConfigChange,
}: DashboardBookingProviderProps) {
  // FULLY CONTROLLED: All state comes from props
  // Default values if not provided in controlledState
  const currentStep = controlledState.currentStep ?? 'service'
  const selectedService = controlledState.selectedService ?? null
  const selectedDate = controlledState.selectedDate ?? null
  const selectedTimeSlot = controlledState.selectedTimeSlot ?? null
  const selectedDateTime = controlledState.selectedDateTime ?? null
  const serviceAddress = controlledState.serviceAddress ?? ''
  const currentMonth = controlledState.currentMonth ?? new Date()
  const customerInfo = controlledState.customerInfo ?? null
  const selectedFieldId = controlledState.selectedFieldId ?? null

  // Confirmation data is local-only (only used on success screen)
  const [confirmationData, setConfirmationData] = useState<any>(null)

  // Loading states (always false in dashboard - data already loaded)
  const servicesLoading = false
  const servicesError = null
  const availabilityLoading = false
  const availabilityError = null
  const bookingLoading = false
  const bookingError = null

  // Auto-select service when only one is available
  useEffect(() => {
    if (services.length === 1 && !selectedService) {
      onStateChange?.({ selectedService: services[0] })
    }
  }, [services, selectedService, onStateChange])

  // Navigation actions
  const goToStep = (step: BookingStep) => {
    onStateChange?.({ currentStep: step })
  }

  const nextStep = () => {
    const steps: BookingStep[] = ['service', 'datetime', 'contact', 'confirm', 'success']
    const currentIndex = steps.indexOf(currentStep)
    if (currentIndex < steps.length - 1) {
      onStateChange?.({ currentStep: steps[currentIndex + 1] })
    }
  }

  const prevStep = () => {
    const steps: BookingStep[] = ['service', 'datetime', 'contact', 'confirm', 'success']
    const currentIndex = steps.indexOf(currentStep)
    if (currentIndex > 0) {
      onStateChange?.({ currentStep: steps[currentIndex - 1] })
    }
  }

  // Service actions (no API calls - data from props)
  const loadServices = async () => {
    // No-op: services already provided via props
    // Auto-select is handled by useEffect above
  }

  const selectService = (service: Service) => {
    onStateChange?.({ selectedService: service })
  }

  const changeService = () => {
    onStateChange?.({ selectedService: null })
  }

  // Availability actions (no API calls - data from props)
  const loadAvailability = async (_year: number, _month: number) => {
    // No-op: availability already provided via props
  }

  const handleSetCurrentMonth = (date: Date) => {
    onStateChange?.({ currentMonth: date })
    // In dashboard, we don't need to load availability - it's already in props
  }

  // Selection actions
  const selectDate = (date: string) => {
    onStateChange?.({
      selectedDate: date,
      selectedTimeSlot: null,
      selectedDateTime: null
    })
  }

  const selectTimeSlot = (isoTime: string, displayTime: string) => {
    if (!selectedDate) return

    onStateChange?.({
      selectedTimeSlot: isoTime,
      selectedDateTime: {
        date: selectedDate,
        time: displayTime,
        iso: isoTime,
        address: serviceAddress,
      }
    })
  }

  const setAddress = (address: string) => {
    const updates: any = { serviceAddress: address }

    // Also update selectedDateTime if it exists
    if (selectedDateTime) {
      updates.selectedDateTime = {
        ...selectedDateTime,
        address,
      }
    }

    onStateChange?.(updates)
  }

  // Customer actions
  const handleSetCustomerInfo = (info: CustomerInfo) => {
    onStateChange?.({ customerInfo: info })
  }

  // Booking action (dashboard preview - don't actually create booking)
  const createBooking = async () => {
    // In dashboard preview, simulate successful booking
    setConfirmationData({
      appointment: {
        id: 'preview-' + Date.now(),
        customer_id: 'preview-customer',
        service_type: selectedService?.name || '',
        scheduled_datetime: selectedDateTime?.iso || '',
        service_address: selectedDateTime?.address || '',
        duration_minutes: selectedService?.base_duration || 0,
        description: `Preview booking for ${selectedService?.name}`,
        status: 'pending',
        created_at: new Date().toISOString(),
        sms_consent: customerInfo?.smsConsent || false,
      },
      customer: {
        id: 'preview-customer',
        name: customerInfo?.name || '',
        email: customerInfo?.email || '',
        phone: customerInfo?.phone || '',
      },
      found_existing_account: false,
      notifications_sent: {
        email: false,
        sms: false,
      },
      preview_mode: true,
    })

    onStateChange?.({ currentStep: 'success' })
  }

  // Inline Editor Actions (Configuration Mode)
  const selectField = (fieldId: string | null) => {
    onStateChange?.({ selectedFieldId: fieldId })
  }

  const updateFieldLabel = (fieldKey: string, newLabel: string) => {
    const currentLabels = config.widgetConfig.field_labels || {}
    onConfigChange?.({
      field_labels: {
        ...currentLabels,
        [`${fieldKey}_label`]: newLabel
      }
    })
  }

  const updateFieldPlaceholder = (fieldKey: string, newPlaceholder: string) => {
    const currentLabels = config.widgetConfig.field_labels || {}
    onConfigChange?.({
      field_labels: {
        ...currentLabels,
        [`${fieldKey}_placeholder`]: newPlaceholder
      }
    })
  }

  const updateSuccessMessage = (newMessage: string) => {
    onConfigChange?.({
      success_message: newMessage
    })
  }

  const updateFieldConfig = (fieldId: string, newConfig: Record<string, any>) => {
    const currentConfigs = config.widgetConfig.field_configs || {}
    const currentFieldConfig = currentConfigs[fieldId] || {}

    onConfigChange?.({
      field_configs: {
        ...currentConfigs,
        [fieldId]: {
          ...currentFieldConfig,
          ...newConfig
        }
      }
    })
  }

  const updateFieldVisibility = (fieldKey: string, visible: boolean) => {
    // Map field keys to their visibility config keys
    const visibilityKeyMap: Record<string, string> = {
      'notes': 'show_notes',
      'address': 'show_address',
      'how_heard': 'show_how_heard',
      'sms_consent': 'show_sms_consent',
    }

    const configKey = visibilityKeyMap[fieldKey]
    if (!configKey) {
      console.warn(`No visibility config for field: ${fieldKey}`)
      return
    }

    onConfigChange?.({
      [configKey]: visible
    })
  }

  const addCustomField = (fieldType: string, step: 'service' | 'contact' | 'datetime') => {
    const currentQuestions = config.widgetConfig.custom_questions || []
    const maxOrder = Math.max(0, ...currentQuestions.map(q => q.display_order || 0))

    // Generate unique ID
    const fieldId = `custom_${fieldType}_${Date.now()}`

    // Field type labels
    const typeLabels: Record<string, string> = {
      text: 'Text Field',
      textarea: 'Long Text Field',
      number: 'Number Field',
      select: 'Dropdown Field',
      checkbox: 'Checkbox Field'
    }

    const newField = {
      id: fieldId,
      type: fieldType,
      label: typeLabels[fieldType] || 'New Field',
      placeholder: 'Enter value...',
      required: false,
      show_on_step: step,
      display_order: maxOrder + 1,
      help_text: '',
      validation: {},
      options: fieldType === 'select' ? [] : undefined
    }

    onConfigChange?.({
      custom_questions: [...currentQuestions, newField]
    })
  }

  const removeCustomField = (fieldId: string) => {
    const currentQuestions = config.widgetConfig.custom_questions || []
    onConfigChange?.({
      custom_questions: currentQuestions.filter(q => q.id !== fieldId)
    })
  }

  // Reset action
  const reset = () => {
    onStateChange?.({
      currentStep: 'service',
      selectedService: services.length === 1 ? services[0] : null,
      selectedDate: null,
      selectedTimeSlot: null,
      selectedDateTime: null,
      serviceAddress: '',
      currentMonth: new Date(),
      customerInfo: null,
      selectedFieldId: null
    })
    setConfirmationData(null)
  }

  // Create the store-like value
  const value = useMemo(
    () => ({
      // Configuration
      config,
      configurationMode,

      // State
      currentStep,
      services,
      selectedService,
      servicesLoading,
      servicesError,
      availability,
      availabilityLoading,
      availabilityError,
      currentMonth,
      selectedDate,
      selectedTimeSlot,
      selectedDateTime,
      serviceAddress,
      customerInfo,
      bookingLoading,
      bookingError,
      confirmationData,

      // Inline Editor State
      selectedFieldId,

      // Actions
      goToStep,
      nextStep,
      prevStep,
      loadServices,
      selectService,
      changeService,
      loadAvailability,
      setCurrentMonth: handleSetCurrentMonth,
      selectDate,
      selectTimeSlot,
      setAddress,
      setCustomerInfo: handleSetCustomerInfo,
      createBooking,
      reset,

      // Inline Editor Actions
      selectField,
      updateFieldLabel,
      updateFieldPlaceholder,
      updateSuccessMessage,
      updateFieldConfig,
      updateFieldVisibility,
      addCustomField,
      removeCustomField,
    }),
    [
      config,
      configurationMode,
      currentStep,
      services,
      selectedService,
      availability,
      currentMonth,
      selectedDate,
      selectedTimeSlot,
      selectedDateTime,
      serviceAddress,
      customerInfo,
      confirmationData,
      selectedFieldId,
    ]
  )

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
}
